import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const norm = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function Mark({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  return <>{text.split(new RegExp(`(${esc(q)})`, "ig")).map((t, i) => (norm(t) === q ? <mark key={i} className="bg-[var(--c-muted)] text-inherit px-0.5">{t}</mark> : <React.Fragment key={i}>{t}</React.Fragment>))}</>;
}

function useHits(items: any[], q: string) {
  return useMemo(() => {
    const all = (items || []).filter((t) => t.term).sort((a, b) => a.term.localeCompare(b.term));
    if (!q) return all;
    const rank = (t: any) => (norm(t.term).startsWith(q) ? 0 : norm(t.term).includes(q) ? 1 : 2);
    return all.filter((t) => norm(t.term).includes(q) || norm(t.definition).includes(q)).sort((a, b) => rank(a) - rank(b) || a.term.localeCompare(b.term));
  }, [items, q]);
}

export function GlossaryBlock({ b }: any) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const nq = norm(q.trim());
  const hits = useHits(b.items, nq);
  const groups = useMemo(() => {
    const g: Record<string, any[]> = {};
    hits.forEach((t: any) => { const L = /[a-z]/i.test(t.term[0]) ? t.term[0].toUpperCase() : "#"; (g[L] = g[L] || []).push(t); });
    return g;
  }, [hits]);
  useEffect(() => { const t = setTimeout(() => setParams(nq ? { q: q.trim() } : {}, { replace: true }), 350); return () => clearTimeout(t); }, [nq]);
  const jump = (L: string) => document.getElementById(`glossary-${L}`)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="max-w-[1200px] mx-auto px-5 md:px-12 py-12 md:py-16" data-testid="glossary-block">
      {(b.heading || b.intro) && <div className="text-center max-w-2xl mx-auto mb-10">{b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl">{b.heading}</h2>}{b.intro && <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-neutral-700">{b.intro}</p>}</div>}
      <div className="sticky top-16 md:top-[124px] z-20 bg-[var(--c-bg)] py-4 border-b border-neutral-200 space-y-3">
        <div className="relative max-w-xl mx-auto">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search terms, e.g. "milgrain" or "asscher"' aria-label="Search the glossary"
            className="w-full border border-neutral-900 bg-white pl-11 pr-11 py-3.5 text-sm outline-none focus:ring-1 focus:ring-neutral-900" data-testid="glossary-search-input" />
          {q && <button onClick={() => setQ("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-neutral-900" data-testid="glossary-search-clear"><X size={16} /></button>}
        </div>
        <div className="flex flex-wrap justify-center gap-x-1 gap-y-1" data-testid="glossary-letters">
          {LETTERS.map((L) => <button key={L} disabled={!groups[L]} onClick={() => jump(L)} className="w-7 h-7 text-xs tracking-wider transition-colors enabled:hover:bg-neutral-900 enabled:hover:text-white disabled:text-neutral-300" data-testid={`glossary-letter-${L}`}>{L}</button>)}
        </div>
        <p className="text-center text-[11px] text-neutral-500" data-testid="glossary-count">{nq ? `${hits.length} ${hits.length === 1 ? "term matches" : "terms match"} “${q.trim()}”` : `${hits.length} terms`}</p>
      </div>
      {!hits.length && (
        <div className="text-center py-16" data-testid="glossary-empty">
          <p className="font-display text-2xl">No terms match “{q.trim()}”</p>
          <p className="text-sm text-neutral-600 mt-3">Try a different spelling, or <Link to="/pages/contact" className="underline">ask our specialists</Link>.</p>
        </div>
      )}
      <div className="divide-y divide-neutral-200">
        {Object.keys(groups).sort().map((L) => (
          <div key={L} id={`glossary-${L}`} className="scroll-mt-64 grid md:grid-cols-[90px_1fr] gap-4 md:gap-8 py-10" data-testid={`glossary-group-${L}`}>
            <p className="font-display text-5xl leading-none">{L}</p>
            <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-7">
              {groups[L].map((t: any) => (
                <div key={t.term} id={`term-${norm(t.term).replace(/[^a-z0-9]+/g, "-")}`} data-testid={`glossary-term-${norm(t.term).replace(/[^a-z0-9]+/g, "-")}`}>
                  <dt className="text-xs tracking-[0.16em] uppercase font-medium"><Mark text={t.term} q={nq} /></dt>
                  <dd className="mt-2 text-sm leading-relaxed text-neutral-700"><Mark text={t.definition || ""} q={nq} /></dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}
